/**
 * Blood Test offer — panel tab switch.
 *
 * Each offer panel is a block; a pill tab selects which one is visible. Visibility
 * is a `hidden` attribute on the panels, kept in sync with `data-active-panel` on
 * the root and `aria-selected` on the tabs.
 *
 * Contract:
 *   [data-blood-test-offer-tabs]     root, carries data-active-panel
 *     [data-offer-tab="<id>"]        <button role="tab">
 *     [data-offer-panel="<id>"]      <div role="tabpanel">
 */

function initOne(root) {
  const tabs = [...root.querySelectorAll('[data-offer-tab]')];
  const panels = [...root.querySelectorAll('[data-offer-panel]')];
  if (tabs.length === 0) return null;

  const keys = panels.map((p) => p.dataset.offerPanel);

  const select = (key) => {
    if (!keys.includes(key)) return;
    root.dataset.activePanel = key;
    tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', String(tab.dataset.offerTab === key));
    });
    panels.forEach((panel) => {
      panel.toggleAttribute('hidden', panel.dataset.offerPanel !== key);
    });
  };

  const onClick = (e) => select(e.currentTarget.dataset.offerTab);

  // Left/Right arrows move between tabs, per the WAI-ARIA tabs pattern.
  const onKeydown = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = tabs.indexOf(e.currentTarget);
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
    next.focus();
    select(next.dataset.offerTab);
  };

  tabs.forEach((tab) => {
    tab.addEventListener('click', onClick);
    tab.addEventListener('keydown', onKeydown);
  });

  select(root.dataset.activePanel || keys[0]);

  return {
    destroy() {
      tabs.forEach((tab) => {
        tab.removeEventListener('click', onClick);
        tab.removeEventListener('keydown', onKeydown);
      });
    },
  };
}

export default function initBloodTestOfferTabs(scope) {
  const root = scope && scope.querySelectorAll ? scope : document;
  const instances = [...root.querySelectorAll('[data-blood-test-offer-tabs]')]
    .map(initOne)
    .filter(Boolean);

  return {
    destroy() {
      instances.forEach((i) => i.destroy());
    },
  };
}
